/**
 * Cleanup utility for corrupted localStorage entries AND migration from old individual keys
 * 
 * This function:
 * 1. Removes corrupted entries that can't be parsed
 * 2. Migrates old individual keys to consolidated keys
 * 3. Removes old keys after successful migration
 */
export function cleanupCorruptedLocalStorage() {
  // Old individual keys that have been consolidated
  const oldIndividualKeys = [
    'blocksystem-blocks',
    'blocksystem-connections',
    'blocksystem-connectionmarkers',
    'blocksystem-systems',
    'blocksystem-textannotations',
    'blocksystem-placedlabels',
    'blocksystem-pipetypes',
    'blocksystem-labeltemplates',
    'blocksystem-projectname',
    'blocksystem-selectedpipetype',
    'blocksystem-drawingfolder',
    'blocksystem-activedrawingid',
    'blocksystem-plans',
    'blocksystem-titleblocktemplates',
    'blocksystem-activeplanid',
    'blocksystem-viewstate',
    'blocksystem-backgroundsettings',
    'blocksystem-viewdisplaysettings',
    'blocksystem-showdimensions',
    'blocksystem-snapenabled',
  ];

  let cleanedCount = 0;
  let migratedCount = 0;
  let totalFreed = 0;

  // Check if we've already migrated (consolidated keys exist)
  const hasConsolidatedData = 
    localStorage.getItem('blocksystem-drawing-data') !== null ||
    localStorage.getItem('blocksystem-project-meta') !== null ||
    localStorage.getItem('blocksystem-plans-data') !== null ||
    localStorage.getItem('blocksystem-view-settings') !== null;

  oldIndividualKeys.forEach(key => {
    try {
      const value = localStorage.getItem(key);
      if (value !== null) {
        const size = value.length;
        
        // Try to parse - if it fails, the data is corrupted
        try {
          JSON.parse(value);
          
          // If data is valid AND we have consolidated data, remove the old key
          if (hasConsolidatedData) {
            localStorage.removeItem(key);
            totalFreed += size;
            migratedCount++;
            console.log(`🔄 Migrated and removed old key: ${key} (${(size / 1024).toFixed(1)}KB)`);
          }
        } catch (parseError) {
          // Data is corrupted, remove it
          console.warn(`🗑️ Removing corrupted localStorage entry: ${key}`);
          localStorage.removeItem(key);
          totalFreed += size;
          cleanedCount++;
        }
      }
    } catch (error) {
      console.error(`Failed to process ${key}:`, error);
    }
  });

  if (cleanedCount > 0) {
    console.log(`✅ Cleaned ${cleanedCount} corrupted localStorage entries`);
  }
  
  if (migratedCount > 0) {
    console.log(`✅ Migrated ${migratedCount} old keys (freed ${(totalFreed / 1024).toFixed(1)}KB)`);
  }

  return { cleanedCount, migratedCount, totalFreed };
}